import {
  AddressState,
  cleanFullAddress,
  PropertyAddress,
  PropertyDetails,
  SolicitorDetails,
  VendorBuyer
} from '@urbanx/agx-ui-components';
import { isEqual, omit } from 'lodash-es';

export enum RexDisconnectedModalFields {
  BlankContract = 'Blank Contract',
  LegalName = 'Legal Name',
  VendorsAddress = 'Vendors Address',
  VendorSolicitor = 'Vendors Solicitor',
  PropertyType = 'Property Type',
  Beds = 'Beds',
  Baths = 'Baths',
  Cars = 'Cars',
  LandArea = 'Land Area',
  InternalArea = 'Internal Area',
  PropertyAddress = 'Property Address',
  BuyersLegalNames = 'Buyers Legal Names',
  BuyersAddress = 'Buyers Address',
  BuyerSolicitor = 'Buyer Solicitor',
  SalePrice = 'Sale Price',
  ContractDate = 'Contract Date',
  InitialDeposit = 'Initial Deposit',
  InitialDepositDueDate = 'Initial Deposit Due Date',
  BalanceDeposit = 'Balance Deposit',
  BalanceDepositDueDate = 'Balance Deposit Due Date',
  SettlementDate = 'Settlement Date',
  ExpectedUnconditionalDate = 'Expected Unconditional Date',
}

export type RexDisconnectedModalFieldValue = string | number | undefined;

export interface RexFieldChangeCheck {
  changed: boolean;
  field: RexDisconnectedModalFields;
  value: RexDisconnectedModalFieldValue;
}

export const getVendorBuyerAddress = (vendor: VendorBuyer): AddressState | undefined => {
  if (vendor.individual?.address) return vendor.individual.address;
  if (vendor.company?.address) return vendor.company.address;
  if (vendor.powerOfAttorney?.address) return vendor.powerOfAttorney.address;
  return undefined;
}

const omitFormattedAddress = (address?: PropertyAddress) => {
  if (!address) return address;
  return omit(address, ['formattedAddress']);
};

export const vendorBuyerAddressesChanged = (prevVendorBuyers: VendorBuyer[], newVendorBuyers: VendorBuyer[]): boolean => {
  const prevAddresses = prevVendorBuyers?.map(getVendorBuyerAddress);
  const newAddresses = newVendorBuyers?.map(getVendorBuyerAddress);

  // Compare only the first vendor's address
  const prevAddress = omitFormattedAddress(prevAddresses && (prevAddresses[0]?.address ?? prevAddresses[0]?.alternate));
  const newAddress = omitFormattedAddress(newAddresses && (newAddresses[0]?.address ?? newAddresses[0]?.alternate));

  return !isEqual(prevAddress, newAddress);
}

export const getRexFieldChangeChecks = (
  id: string,
  value: any,
  prevFormValues: any
): RexFieldChangeCheck[] => {
  const checks: RexFieldChangeCheck[] = [];

  if (id === 'Vendors') {
    const prevVendors = prevFormValues['Vendors'] as VendorBuyer[] ?? [];
    const newVendors = value as VendorBuyer[];
    const prevLegalNames = prevVendors?.map(v => v.legalName).join(' & ');
    const newLegalNames = newVendors?.map(v => v.legalName).join(' & ');

    checks.push({
      changed: !isEqual(prevLegalNames, newLegalNames),
      field: RexDisconnectedModalFields.LegalName,
      value: newLegalNames,
    });

    checks.push({
      changed: vendorBuyerAddressesChanged(prevVendors, newVendors),
      field: RexDisconnectedModalFields.VendorsAddress,
      value: newVendors[0]
        ? cleanFullAddress(getVendorBuyerAddress(newVendors[0])?.address ?? getVendorBuyerAddress(newVendors[0])?.alternate)
        : '',
    });
  } else if (id === 'VendorSolicitor') {
    const prevVendorSolicitor = prevFormValues['VendorSolicitor'] as SolicitorDetails;
    const newVendorSolicitor = value as SolicitorDetails;

    checks.push({
      changed: !isEqual(prevVendorSolicitor, newVendorSolicitor),
      field: RexDisconnectedModalFields.VendorSolicitor,
      value: newVendorSolicitor.solicitor?.solicitorName,
    });
  } else if (id === 'PropertyAttributes') {
    const prevPropertyAttr = prevFormValues['PropertyAttributes'] as PropertyDetails;
    const newPropertyAttr = value as PropertyDetails;

    const propertyChecks: Array<[RexDisconnectedModalFields, RexDisconnectedModalFieldValue, RexDisconnectedModalFieldValue]> = [
      [RexDisconnectedModalFields.PropertyType, prevPropertyAttr?.propertyType, newPropertyAttr?.propertyType],
      [RexDisconnectedModalFields.Beds, prevPropertyAttr?.numberOfBeds, newPropertyAttr?.numberOfBeds],
      [RexDisconnectedModalFields.Baths, prevPropertyAttr?.numberOfBathrooms, newPropertyAttr?.numberOfBathrooms],
      [RexDisconnectedModalFields.Cars, prevPropertyAttr?.numberOfCarSpaces, newPropertyAttr?.numberOfCarSpaces],
      [RexDisconnectedModalFields.LandArea, prevPropertyAttr?.landAreaDetails?.totalLandArea, newPropertyAttr?.landAreaDetails?.totalLandArea],
      [RexDisconnectedModalFields.InternalArea, prevPropertyAttr?.landAreaDetails?.internalArea, newPropertyAttr?.landAreaDetails?.internalArea],
    ];

    propertyChecks.forEach(([field, prevVal, newVal]) => {
      checks.push({
        changed: !isEqual(prevVal, newVal),
        field,
        value: newVal,
      });
    });
  } else if (id === 'PropertyAddress') {
    const prevAddress = prevFormValues['PropertyAddress'] as PropertyAddress;
    const newAddress = value as PropertyAddress;

    checks.push({
      changed: !isEqual(prevAddress, newAddress),
      field: RexDisconnectedModalFields.PropertyAddress,
      value: cleanFullAddress(newAddress),
    });
  }

  return checks;
}